import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  TextField,
  Box,
  IconButton,
  Chip,
  InputAdornment,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  Add,
  Search,
  Visibility,
  Edit,
  Flight,
  Description,
  Warning,
  CheckCircle,
  EuroSymbol
} from '@mui/icons-material';
import api from '../services/api';

const Postings = () => {
  const navigate = useNavigate();
  const [postings, setPostings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');

  // EU Minimum Wages 2025
  const minimumWages = {
    'DE': { hourly: 12.00, monthly: 2080, country: 'Germany' },
    'NL': { hourly: 13.27, monthly: 2300, country: 'Netherlands' },
    'BE': { hourly: 11.50, monthly: 1990, country: 'Belgium' },
    'FR': { hourly: 11.65, monthly: 2020, country: 'France' },
    'LU': { hourly: 15.50, monthly: 2687, country: 'Luxembourg' },
    'AT': { hourly: 11.00, monthly: 1900, country: 'Austria' },
    'IE': { hourly: 12.70, monthly: 2200, country: 'Ireland' },
    'ES': { hourly: 7.82, monthly: 1350, country: 'Spain' },
    'IT': { hourly: 9.50, monthly: 1650, country: 'Italy' },
    'PL': { hourly: 5.85, monthly: 1015, country: 'Poland' }
  };

  useEffect(() => {
    fetchPostings();
  }, []);

  const fetchPostings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/postings');
      setPostings(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching postings:', error);
      setError('Failed to load postings');
      setPostings([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredPostings = postings.filter(posting => {
    const searchLower = searchTerm.toLowerCase();
    const employeeName = `${posting.employee_first_name} ${posting.employee_last_name}`.toLowerCase();
    const hostCountry = posting.host_country?.toLowerCase() || '';
    const hostCompany = posting.host_company_name?.toLowerCase() || '';
    const postingNumber = posting.posting_number?.toLowerCase() || '';
    
    return employeeName.includes(searchLower) || 
           hostCountry.includes(searchLower) ||
           hostCompany.includes(searchLower) ||
           postingNumber.includes(searchLower);
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const getPostingStatus = (posting) => {
    const today = new Date();
    const startDate = new Date(posting.posting_start_date);
    const endDate = new Date(posting.posting_end_date);
    
    if (posting.status === 'cancelled') return { status: 'Cancelled', color: 'default' };
    if (posting.status === 'completed') return { status: 'Completed', color: 'default' };
    
    if (today < startDate) {
      const daysUntilStart = Math.ceil((startDate - today) / (1000 * 60 * 60 * 24));
      return { status: `Upcoming (${daysUntilStart} days)`, color: 'info' };
    }
    
    if (today > endDate) {
      return { status: 'Expired', color: 'error' };
    }
    
    const daysRemaining = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
    if (daysRemaining <= 30) {
      return { status: `Ending Soon (${daysRemaining} days)`, color: 'warning' };
    }
    
    return { status: 'Active', color: 'success' };
  };

  const getA1Status = (posting) => {
    if (!posting.a1_form_number) {
      return { icon: <Warning color="error" />, text: 'A1 Required', color: 'error' };
    }
    
    if (posting.a1_expiry_date) {
      const expiryDate = new Date(posting.a1_expiry_date);
      const today = new Date();
      
      if (expiryDate < today) {
        return { icon: <Warning color="error" />, text: 'A1 Expired', color: 'error' };
      }
      
      const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
      if (daysUntilExpiry <= 30) {
        return { icon: <Warning color="warning" />, text: `A1 Expiring (${daysUntilExpiry} days)`, color: 'warning' };
      }
    }
    
    return { icon: <CheckCircle color="success" />, text: 'A1 Valid', color: 'success' };
  };

  const checkMinimumWageCompliance = (posting) => {
    const countryWage = minimumWages[posting.host_country];
    if (!countryWage) return null;
    
    const actualWage = parseFloat(posting.actual_wage || 0);
    const minWage = posting.actual_wage_type === 'hourly' ? countryWage.hourly : countryWage.monthly;
    
    if (actualWage < minWage) {
      return { compliant: false, minWage, actualWage };
    }
    
    return { compliant: true, minWage, actualWage };
  };

  // Statistics
  const activePostings = filteredPostings.filter(p => getPostingStatus(p).status === 'Active').length;
  const upcomingPostings = filteredPostings.filter(p => getPostingStatus(p).status.includes('Upcoming')).length;
  const expiredPostings = filteredPostings.filter(p => getPostingStatus(p).status === 'Expired').length;
  const a1Issues = filteredPostings.filter(p => ['error', 'warning'].includes(getA1Status(p).color)).length;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          EU Worker Postings
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/admin/postings/new')}
        >
          New Posting
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle color="success" />
                <Box>
                  <Typography variant="h6">{activePostings}</Typography>
                  <Typography variant="body2" color="text.secondary">Active Postings</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Flight color="info" />
                <Box>
                  <Typography variant="h6">{upcomingPostings}</Typography>
                  <Typography variant="body2" color="text.secondary">Upcoming</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Warning color="warning" />
                <Box>
                  <Typography variant="h6">{a1Issues}</Typography>
                  <Typography variant="body2" color="text.secondary">A1 Issues</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Description color="action" />
                <Box>
                  <Typography variant="h6">{filteredPostings.length}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Postings</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by employee name, country, company, or posting number..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Posting Number</TableCell>
              <TableCell>Employee</TableCell>
              <TableCell>Host Country</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Host Company</TableCell>
              <TableCell>Wage Compliance</TableCell>
              <TableCell>A1 Status</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  Loading postings...
                </TableCell>
              </TableRow>
            ) : filteredPostings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  {searchTerm ? 'No postings found matching your search' : 'No postings found'}
                </TableCell>
              </TableRow>
            ) : (
              filteredPostings.map((posting) => {
                const status = getPostingStatus(posting);
                const a1Status = getA1Status(posting);
                const wageCompliance = checkMinimumWageCompliance(posting);
                
                return (
                  <TableRow key={posting.id} hover>
                    <TableCell>
                      <Box
                        component="span"
                        sx={{ 
                          cursor: 'pointer', 
                          color: 'primary.main',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={() => navigate(`/admin/postings/${posting.id}`)}
                      >
                        {posting.posting_number || `POST-${posting.id}`}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={() => navigate(`/admin/employees/${posting.employee_id}`)}
                      >
                        {posting.employee_first_name} {posting.employee_last_name}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img 
                          src={`https://flagcdn.com/24x18/${posting.host_country?.toLowerCase()}.png`}
                          alt={posting.host_country}
                          style={{ width: 24, height: 18 }}
                          onError={(e) => { e.target.style.display = 'none' }}
                        />
                        {minimumWages[posting.host_country]?.country || posting.host_country}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(posting.posting_start_date)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        to {formatDate(posting.posting_end_date)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {posting.host_company_name || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {wageCompliance ? (
                        <Box>
                          {wageCompliance.compliant ? (
                            <Chip
                              label="Compliant"
                              size="small"
                              color="success"
                              icon={<CheckCircle />}
                            />
                          ) : (
                            <Chip
                              label="Below Min"
                              size="small"
                              color="error"
                              icon={<Warning />}
                            />
                          )}
                          <Typography variant="caption" display="block">
                            €{wageCompliance.actualWage}/{wageCompliance.minWage}
                          </Typography>
                        </Box>
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {a1Status.icon}
                        <Typography variant="body2">
                          {posting.a1_form_number || 'Missing'}
                        </Typography>
                      </Box>
                      {posting.a1_expiry_date && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          Exp: {formatDate(posting.a1_expiry_date)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={status.status} 
                        color={status.color}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/admin/postings/${posting.id}`)}
                        title="View Details"
                      >
                        <Visibility />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/admin/postings/edit/${posting.id}`)}
                        title="Edit Posting"
                      >
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Minimum Wage Reference */}
      <Paper sx={{ mt: 3, p: 2 }}>
        <Typography variant="h6" gutterBottom>
          EU Minimum Wages Reference (2025)
        </Typography>
        <Grid container spacing={2}>
          {Object.entries(minimumWages).map(([code, data]) => (
            <Grid item xs={6} md={2.4} key={code}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <img 
                  src={`https://flagcdn.com/24x18/${code.toLowerCase()}.png`}
                  alt={code}
                  style={{ width: 24, height: 18 }}
                />
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    {data.country}
                  </Typography>
                  <Typography variant="caption">
                    €{data.hourly}/hr | €{data.monthly}/mo
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </Container>
  );
};

export default Postings;
